import { useEffect, useRef, useState } from 'react';
import type { CateringLine } from '@tikkas/shared';
import { IC, Svg } from '../art/icons.js';
import { burst, PALETTE } from '../art/bowl.js';
import { api } from '../lib/api.js';
import { fmt, hhmmLocal, num, reduced } from '../lib/format.js';
import { useNav } from '../lib/router.js';
import { useStore } from '../lib/store.js';
import { Scroll, Sheet } from '../components/ui.js';
import { Stepper } from '../components/Catering.js';
import { NewCardForm, payNewCard, paySavedCard, type NewCardHandle } from '../components/Pay.js';
import { cateringApi, loadReorder, type CateringView, type ChatMessage } from '../lib/catering.js';
import { Browser } from '@capacitor/browser';
import { APP_BUILD } from '../lib/platform.js';

const REQUEST_KINDS: [string, string, string][] = [['headcount', 'Number of people', 'e.g. Two more people, one Classic chicken, one Malai paneer'], ['time', 'Delivery time', 'e.g. Can it come at 12:40 instead?'], ['items', 'Items or flavours', 'e.g. Swap two Angara for Hariyali'], ['address', 'Address or access', 'e.g. Deliver to the 4th floor instead'], ['other', 'Something else', 'Tell us what you need']];
const kindLabel = (k: string) => REQUEST_KINDS.find((x) => x[0] === k)?.[1] ?? (k === 'quantity_drop' ? 'Fewer boxes' : k);

/** One catering order: where it is, the timeline, chat, changes, the invoice, the box labels, cancelling and booking again. */
export function CateringOrder() {
  const s = useStore();
  const { route, navigate } = useNav();
  const id = route.params.id ?? '';
  const [v, setV] = useState<CateringView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [booked, setBooked] = useState(() => route.search.get('booked') === '1');
  const [sheet, setSheet] = useState<'labels' | 'chat' | 'change' | 'cancel' | 'drop' | null>(null);
  const [labels, setLabels] = useState<{ n: number; name: string | null; item: string; flavour: string | null; protein: string | null; allergens: string[]; note: string | null }[]>([]);
  const load = () => cateringApi.order(id).then(setV).catch((e: Error) => setErr(e.message));
  useEffect(() => {
    if (!s.me) return;
    load();
    const t = window.setInterval(() => document.visibilityState === 'visible' && load(), 15_000);
    return () => clearInterval(t);
  }, [id, s.me?.customer.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!s.me) {
    navigate('/join?next=' + encodeURIComponent(route.path), { replace: true });
    return null;
  }
  if (err) return <Scroll><p className="err">{err}</p><button type="button" className="btn2" onClick={() => navigate('/catering/orders', { root: true })}>Your orders</button></Scroll>;
  if (!v) return <Scroll><p className="muted">Loading your order…</p></Scroll>;
  const c = v.catering, o = v.order;
  const stageIdx = c.timeline.findIndex((t) => t.now);
  const done = c.stage === 'delivered';
  const bad = c.stage === 'cancelled' || c.stage === 'refused';
  const deliveredAt = c.timeline.find((t) => t.stage === 'delivered')?.at ?? null;
  const head = c.stage === 'refused' ? 'We could not book this' : c.stage === 'cancelled' ? 'Cancelled' : c.stage === 'booked' ? c.dateLong : c.stage === 'kitchen' ? 'Being prepared' : c.stage === 'delivered' ? (c.method === 'delivery' ? `Delivered${deliveredAt ? ` at ${hhmmLocal(deliveredAt)}` : ''}` : 'Collected') : c.method === 'delivery' ? (c.eta ? `Arriving ${c.eta}` : 'Packed and labelled') : 'Ready to collect';
  const sub = c.stage === 'refused' ? `${c.refusedReason ?? 'The slot was taken while you paid.'} Your payment has been refunded in full.` : c.stage === 'cancelled' ? (o.refundedPence ? `${fmt(o.refundedPence)} is on its way back to your card.` : 'Nothing was refunded under the cancellation policy.') : c.stage === 'booked' ? `${c.method === 'delivery' ? `${c.slotLabel} to ${c.address?.name ?? 'your address'}` : 'Collection from 11:45'}. ${c.policy.text}` : c.stage === 'kitchen' ? 'Our kitchen is cooking now. Everything is packed and labelled before your window.' : c.stage === 'packed' ? `${c.boxes} box${c.boxes === 1 ? '' : 'es'} packed and labelled. ${c.method === 'delivery' ? 'The rider is loading the cargo bike.' : `Collect from the counter, it's under ${c.company}.`}` : c.stage === 'out' ? (c.method === 'delivery' ? `The rider is on the way${c.eta ? `, arriving about ${c.eta}` : ''}.` : 'Ready at the counter.') : `Handed over at ${c.address?.floor || 'reception'}. Enjoy your lunch.`;
  const invoiceUrl = `/api/catering/orders/${o.id}/invoice.pdf`;
  // The app has no cookie for the in-app browser to send: it asks for a ten-minute link signed for this order and opens that.
  const openInvoice = async () => {
    if (!APP_BUILD) return void window.open(invoiceUrl, '_blank', 'noopener');
    try {
      const r = await api<{ url: string }>(`/api/catering/orders/${o.id}/invoice-link`);
      await Browser.open({ url: r.url, presentationStyle: 'popover' });
    } catch {
      s.toast('Could not open the invoice');
    }
  };
  const unpaidExtra = (v.extras ?? []).find((x) => x.status === 'pending_payment') ?? null;
  const showLabels = async () => {
    try {
      const r = await api<{ boxes: typeof labels }>(`/api/catering/orders/${o.id}/labels`);
      setLabels(r.boxes);
      setSheet('labels');
    } catch {
      s.toast('Could not load the labels');
    }
  };
  const again = async () => {
    try {
      await loadReorder(o.id);
      navigate('/catering', { root: true });
      s.toast('Same order loaded. Just pick a day.');
    } catch (e) {
      s.toast(e instanceof Error ? e.message : 'Could not load that order');
    }
  };
  return (
    <>
      <Scroll className="cpage">
        <div className="topbar"><button type="button" className="round" onClick={() => navigate('/catering/orders', { root: true })} aria-label="Back"><Svg html={IC.back} /></button><span className="grow col" style={{ gap: 0 }}><span className="eyebrow">Catering · #{o.number}{c.invoiceNumber ? ` · ${c.invoiceNumber}` : ''}</span><b style={{ fontSize: 16 }}>{c.company}</b></span></div>
        <div className="cgridpage"><div className="col" style={{ gap: 18, minWidth: 0 }}>
          <section className={`lh ${!bad && !done && stageIdx >= 2 ? 'ready' : ''}`}>
            <span className="eyebrow" style={!bad && !done && stageIdx >= 2 ? { color: 'rgba(20,18,16,.7)' } : undefined}>{!bad && !done && stageIdx >= 2 ? (c.eta ? 'Exact time, texted at pack-out' : 'Packed') : done || bad ? c.dateLong : 'Booked'}</span>
            <h1 className="display">{head}</h1>
            <p>{sub}</p>
          </section>
          {unpaidExtra && <div className="dbl" style={{ borderColor: 'var(--yellow)' }}><Svg html={IC.spark} /><span className="grow">A change costs <b>{fmt(unpaidExtra.totalPence)}</b> more. Pay it and the change is confirmed.</span></div>}
          {unpaidExtra && <PayExtra orderId={unpaidExtra.id} amountPence={unpaidExtra.totalPence} onPaid={load} />}
          {!bad && (
            <ol className="steps">{c.timeline.map((t, i) => <li key={t.stage}><span className="rail"><span className={`node ${t.done ? 'done' : ''} ${t.now ? 'now' : ''}`}>{t.done ? <Svg html={IC.tick} /> : t.now ? <i /> : null}</span>{i === c.timeline.length - 1 ? null : <span className={`bar ${t.done ? 'done' : ''}`} />}</span><span className="lab" style={{ fontWeight: t.now ? 700 : undefined, color: !t.done && !t.now ? 'var(--muted)' : undefined }}><span>{t.label}</span><span className="muted tnum">{t.at ? hhmmLocal(t.at) : ''}</span></span></li>)}</ol>
          )}
          <div className="cacts">
            <button type="button" disabled={!c.canChange} onClick={() => setSheet('change')}><span className="ricon"><Svg html={IC.spark} /></span><b>Request a change</b><span>{c.canChange ? 'Headcount, time, items' : bad ? 'Not available' : 'On its way now'}</span></button>
            <button type="button" onClick={() => setSheet('chat')}><span className="ricon"><Svg html={IC.people} /></span><b>Message us</b><span>{v.unreadMessages ? `${v.unreadMessages} new repl${v.unreadMessages === 1 ? 'y' : 'ies'}` : 'The catering team replies here'}</span></button>
            <button type="button" onClick={showLabels}><span className="ricon"><Svg html={IC.gift} /></span><b>Box labels</b><span>Names and allergens</span></button>
            <button type="button" onClick={openInvoice} disabled={!c.invoiceNumber}><span className="ricon"><Svg html={IC.wallet} /></span><b>VAT invoice</b><span>{c.invoiceNumber ? (c.poNumber ? `PO ${c.poNumber} · PDF` : 'PDF, ready now') : 'Once booked'}</span></button>
          </div>
          {c.canChange && c.freeDropMains > 0 && <div className="cnote"><Svg html={IC.tick} />Up to {c.freeDropMains} main{c.freeDropMains === 1 ? '' : 's'} can come off free until the cut-off, refunded straight away. <button type="button" className="ledit" onClick={() => setSheet('drop')}>Fewer boxes</button></div>}
          {v.requests.length > 0 && (
            <section className="sec col" style={{ gap: 10 }}><h2 className="h3">Your requests</h2><div className="col" style={{ gap: 10 }}>{v.requests.map((r) => <div className={`creq ${r.status}`} key={r.id}><div className="row" style={{ justifyContent: 'space-between', gap: 10 }}><b>{kindLabel(r.kind)}</b><span className={`badge ${r.status === 'approved' || r.status === 'applied' ? 'good' : r.status === 'refused' ? 'out' : ''}`}>{r.status === 'pending' ? 'Waiting for Tikkas' : r.status === 'refused' ? 'Not possible' : 'Done'}</span></div><span className="small soft">“{r.text}”</span>{r.reply && <span className="small"><b>Tikkas:</b> {r.reply}</span>}{r.refundPence ? <span className="small good">{fmt(r.refundPence)} refunded to your card</span> : null}{r.extraPence && r.extraOrderId && unpaidExtra?.id === r.extraOrderId ? <span className="small">{fmt(r.extraPence)} to pay above</span> : null}</div>)}</div></section>
          )}
          {!bad && c.policy.window !== 'none' && (c.stage === 'booked' || c.stage === 'kitchen') && <button type="button" className="link" style={{ alignSelf: 'flex-start', color: 'var(--muted)' }} onClick={() => setSheet('cancel')}>Cancel this order</button>}
        </div><aside className="caside"><div className="csum">
          <b className="ctot-h">What's coming</b>
          <div className="csrows">{v.lines.map((l) => <div key={l.id}><span>{l.qty} × {l.name}{l.allocations.length ? <><br /><span className="small muted">{l.allocations.map((a) => `${a.qty}× ${[a.flavour, a.protein].filter(Boolean).join(' ')}${a.eaterName ? ` (${a.eaterName})` : ''}`).join(', ')}</span></> : null}</span><span className="tnum">{fmt(l.linePence)}</span></div>)}{c.method === 'delivery' && <div><span>Delivery</span><span className="good">Free</span></div>}</div>
          <div className="ctot"><span>{o.refundedPence ? 'Paid, net of refunds' : 'Paid'}</span><b>{fmt(c.paidPence)}</b></div>
          <span className="small muted">{c.paidParts.length > 1 ? `Paid in ${c.paidParts.length} parts: ${c.paidParts.map(fmt).join(' + ')} · ` : ''}{o.payLabel || 'Card'}{o.refundedPence ? ` · ${fmt(o.refundedPence)} refunded` : ''} · VAT {fmt(Math.round(c.paidPence / 6))}</span>
          {o.pointsToEarn > 0 && <span className="small muted">{done ? `+${num(o.pointsEarned ?? o.pointsToEarn)} points added` : `+${num(o.pointsToEarn)} points on ${c.method === 'delivery' ? 'delivery' : 'collection'}`}</span>}
          <div className="csrows">{c.address && <><div><span>Deliver to</span><span>{c.address.name}</span></div>{c.address.floor && <div><span>Floor</span><span>{c.address.floor}</span></div>}</>}<div><span>Contact</span><span>{c.contactName} · {c.contactMobile}</span></div>{c.notes && <div><span>Note</span><span>{c.notes}</span></div>}</div>
          <button type="button" className="btn2" style={{ minHeight: 44 }} onClick={again}>Book again for another day</button>
        </div></aside></div>
      </Scroll>
      {booked && <BookedFx boxes={c.boxes} when={`${c.dateLong}${c.method === 'delivery' ? ` · ${c.slotLabel}` : ' · collection'}`} refused={c.stage === 'refused'} onDone={() => setBooked(false)} />}
      {sheet === 'labels' && (
        <Sheet onClose={() => setSheet(null)}>
          <h2 className="h2">Box labels</h2>
          <p className="soft" style={{ margin: 0, fontSize: 13.5 }}>Printed on every lid. Allergens in bold.</p>
          {labels[0] && <div className="clabel"><b>{labels[0].name ?? c.company}</b><span>{labels[0].item}{labels[0].flavour ? ` · ${labels[0].flavour} ${labels[0].protein ?? ''}` : ''}</span><span>Contains: <b>{labels[0].allergens.length ? labels[0].allergens.map((a) => a.toUpperCase()).join(', ') : 'NO LISTED ALLERGENS'}</b></span>{labels[0].note && <span>Note: {labels[0].note}</span>}<small>{s.config?.site.name ?? 'Tikkas'} · {s.config?.site.address} · HMC halal</small></div>}
          {labels.length > 1 && <div className="card rows">{labels.slice(1, 60).map((b) => <div className="r" key={b.n}><span className="grow col" style={{ gap: 1 }}><b>{b.name ?? `Box ${b.n}`}</b><span className="s">{b.item}{b.flavour ? ` · ${b.flavour} ${b.protein ?? ''}` : ''}{b.note ? ` · ${b.note}` : ''}</span></span></div>)}</div>}
          <button type="button" className="plain" onClick={() => setSheet(null)}>Close</button>
        </Sheet>
      )}
      {sheet === 'chat' && <ChatSheet orderId={o.id} firstName={s.me.customer.firstName} onClose={() => { setSheet(null); load(); }} />}
      {sheet === 'change' && <ChangeSheet orderId={o.id} onClose={() => setSheet(null)} onSent={() => { setSheet(null); load(); }} />}
      {sheet === 'drop' && <DropSheet view={v} onClose={() => setSheet(null)} onDone={() => { setSheet(null); load(); }} />}
      {sheet === 'cancel' && <CancelSheet view={v} onClose={() => setSheet(null)} onDone={() => { setSheet(null); load(); }} />}
    </>
  );
}

function ChatSheet({ orderId, firstName, onClose }: { orderId: string; firstName: string; onClose: () => void }) {
  const [msgs, setMsgs] = useState<ChatMessage[] | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);
  const load = () => api<{ messages: ChatMessage[] }>(`/api/catering/orders/${orderId}/messages`).then((r) => setMsgs(r.messages)).catch(() => undefined);
  useEffect(() => {
    load();
    const t = window.setInterval(() => document.visibilityState === 'visible' && load(), 10_000);
    return () => clearInterval(t);
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    logRef.current?.scrollTo(0, logRef.current.scrollHeight);
  }, [msgs?.length]);
  const send = async (t: string) => {
    if (!t.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api<{ messages: ChatMessage[] }>(`/api/catering/orders/${orderId}/messages`, { body: { text: t } });
      setMsgs(r.messages);
      setText('');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet onClose={onClose} footer={<form className="codebox" onSubmit={(e) => { e.preventDefault(); send(text); }}><input type="text" autoComplete="off" placeholder="Type a message" aria-label="Message" value={text} maxLength={1000} onChange={(e) => setText(e.target.value)} /><button type="submit" disabled={busy}>Send</button></form>}>
      <div className="row" style={{ gap: 12 }}><span className="appicon" style={{ width: 44, height: 44, borderRadius: 12, overflow: 'hidden', background: '#ffd84d', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img src="/logo.webp" alt="" style={{ width: 34 }} /></span><span className="grow col" style={{ gap: 1 }}><h2 className="h2" style={{ margin: 0 }}>Catering help</h2><span className="small muted">The catering team replies here, usually within minutes in opening hours</span></span></div>
      <div className="chat" ref={logRef}>
        {msgs && !msgs.some((m) => m.from !== 'system') && <div className="cb them">Hi {firstName}, it's the Tikkas catering team. Ask us anything about your order.</div>}
        {msgs?.map((m) => <div className={`cb ${m.from === 'customer' ? 'me' : 'them'}`} key={m.id} style={m.from === 'system' ? { opacity: 0.8 } : undefined}>{m.text}<i>{m.from === 'staff' && m.author ? `${m.author} · ` : ''}{hhmmLocal(m.at)}</i></div>)}
      </div>
      <div className="chips" style={{ flexWrap: 'nowrap', overflowX: 'auto', scrollbarWidth: 'none' }}>{['Are the boxes labelled?', 'Can I add 2 more bowls?', 'When will it arrive?', 'Vegetarian options?', 'Add a PO to the invoice'].map((q) => <button type="button" className="chip mchip" style={{ flexShrink: 0, minHeight: 40 }} key={q} onClick={() => send(q)}>{q}</button>)}</div>
    </Sheet>
  );
}

function ChangeSheet({ orderId, onClose, onSent }: { orderId: string; onClose: () => void; onSent: () => void }) {
  const s = useStore();
  const [kind, setKind] = useState('headcount');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const send = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ message: string }>(`/api/catering/orders/${orderId}/changes`, { body: { kind, text } });
      s.toast(r.message);
      onSent();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not send');
    } finally {
      setBusy(false);
    }
  };
  const ph = REQUEST_KINDS.find((k) => k[0] === kind)?.[2] ?? '';
  return (
    <Sheet onClose={onClose}>
      <h2 className="h2">Request a change</h2>
      <p className="soft" style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>Free until the cut-off. We confirm here, usually within 10 minutes. Anything that costs more is a small extra you pay from this page; anything less is refunded to your card.</p>
      <div className="chips grid2">{REQUEST_KINDS.map(([k, l]) => <button type="button" className="chip mchip" key={k} aria-pressed={kind === k} onClick={() => setKind(k)}>{l}</button>)}</div>
      <label className="col" style={{ gap: 6, fontSize: 13, fontWeight: 600 }}>What would you like to change?<textarea className="note-in" placeholder={ph} value={text} maxLength={500} onChange={(e) => setText(e.target.value)} /></label>
      {err && <p className="err" style={{ margin: 0 }}>{err}</p>}
      <button type="button" className="yellow" disabled={busy || text.trim().length < 3} onClick={send}>Send request</button>
    </Sheet>
  );
}

/** Fewer boxes: within 10% before the cut-off it applies at once and the difference is refunded; beyond that it becomes a request. */
function DropSheet({ view, onClose, onDone }: { view: CateringView; onClose: () => void; onDone: () => void }) {
  const s = useStore();
  const [lines, setLines] = useState<CateringLine[]>(() => view.lines.map((l) => ({ id: l.id, itemId: l.itemId, qty: l.qty, allocations: l.allocations.map((a) => ({ protein: a.protein as never, flavour: a.flavour as never, qty: a.qty, eaterName: a.eaterName, dietaryNote: a.dietaryNote })) })));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dropOne = (lineId: string) => setLines((ls) => ls.map((l) => {
    if (l.id !== lineId || l.qty <= 1) return l;
    // Take one off the last allocation that still has some (a named box goes last).
    const allocs = l.allocations.map((a) => ({ ...a }));
    for (let i = allocs.length - 1; i >= 0; i--) {
      if (allocs[i]!.qty > 0) {
        allocs[i]!.qty -= 1;
        break;
      }
    }
    return { ...l, qty: l.qty - 1, allocations: allocs.filter((a) => a.qty > 0) };
  }));
  const addOne = (lineId: string) => setLines((ls) => ls.map((l) => {
    const orig = view.lines.find((x) => x.id === lineId)!;
    if (l.id !== lineId || l.qty >= orig.qty) return l;
    const allocs = orig.allocations.map((a) => ({ protein: a.protein as never, flavour: a.flavour as never, qty: a.qty, eaterName: a.eaterName, dietaryNote: a.dietaryNote }));
    // Restore towards the original split, one at a time, from the front.
    const cur = l.allocations.map((a) => ({ ...a }));
    for (const a of allocs) {
      const have = cur.filter((x) => x.flavour === a.flavour && x.protein === a.protein && x.eaterName === a.eaterName).reduce((n, x) => n + x.qty, 0);
      if (have < a.qty) {
        const hit = cur.find((x) => x.flavour === a.flavour && x.protein === a.protein && x.eaterName === a.eaterName);
        if (hit) hit.qty += 1;
        else cur.push({ ...a, qty: 1 });
        break;
      }
    }
    return { ...l, qty: l.qty + 1, allocations: cur };
  }));
  const removed = view.lines.reduce((n, l) => n + l.qty, 0) - lines.reduce((n, l) => n + l.qty, 0);
  const send = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ applied: boolean; refundPence: number; message: string }>(`/api/catering/orders/${view.order.id}/changes`, { body: { kind: 'quantity_drop', lines: lines.filter((l) => l.qty > 0) } });
      s.toast(r.applied ? `Done. ${fmt(r.refundPence)} is on its way back to your card.` : r.message);
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not change the order');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet onClose={onClose} footer={<><button type="button" className="yellow" disabled={busy || removed === 0} onClick={send}>{removed === 0 ? 'Take some off first' : removed <= view.catering.freeDropMains ? `Take ${removed} off · refunded now` : `Ask to take ${removed} off`}</button><button type="button" className="plain" onClick={onClose}>Cancel</button></>}>
      <h2 className="h2">Fewer boxes</h2>
      <p className="soft" style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>Up to {view.catering.freeDropMains} main{view.catering.freeDropMains === 1 ? '' : 's'} can come off free before the cut-off, refunded straight away. More than that goes to the team as a request.</p>
      <div className="card rows">{lines.map((l) => { const orig = view.lines.find((x) => x.id === l.id)!; return <div className="r" key={l.id}><span className="grow col" style={{ gap: 2, minWidth: 0 }}><b>{orig.name}</b><span className="s">{orig.qty} booked{l.qty < orig.qty ? ` · ${orig.qty - l.qty} off` : ''}</span></span><Stepper value={l.qty} min={1} max={orig.qty} label={orig.name} onChange={(v) => (v < l.qty ? dropOne(l.id) : addOne(l.id))} /></div>; })}</div>
      {err && <p className="err" style={{ margin: 0 }}>{err}</p>}
    </Sheet>
  );
}

function CancelSheet({ view, onClose, onDone }: { view: CateringView; onClose: () => void; onDone: () => void }) {
  const s = useStore();
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const p = view.catering.policy;
  const go = async () => {
    setBusy(true);
    try {
      const r = await api<{ refundPence: number }>(`/api/catering/orders/${view.order.id}/cancel`, { body: { reason } });
      s.toast(r.refundPence ? `Cancelled. ${fmt(r.refundPence)} is on its way back to your card.` : 'Cancelled.');
      onDone();
    } catch (e) {
      s.toast(e instanceof Error ? e.message : 'Could not cancel');
      setBusy(false);
    }
  };
  return (
    <Sheet onClose={onClose} footer={<><button type="button" className="yellow" disabled={busy} onClick={go}>{p.refundPence ? `Cancel and refund ${fmt(p.refundPence)}` : 'Cancel without a refund'}</button><button type="button" className="plain" onClick={onClose}>Keep the order</button></>}>
      <h2 className="h2">Cancel this order?</h2>
      <p className="soft" style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>{p.text} {p.window === 'full' ? `You'd get the full ${fmt(p.refundPence)} back.` : p.window === 'half' ? `You'd get ${fmt(p.refundPence)} back (half).` : ''}</p>
      <input className="txt-in" placeholder="Why? (optional, it helps us)" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />
    </Sheet>
  );
}

/** An approved change that costs more: a small extra order, paid with a saved card or a new one (card only, like the booking). */
function PayExtra({ orderId, amountPence, onPaid }: { orderId: string; amountPence: number; onPaid: () => void }) {
  const s = useStore();
  const u = s.me?.customer;
  const pk = s.config?.stripe.publishableKey ?? '';
  const accountId = s.config?.site.stripeAccountId ?? '';
  const stripeOn = !!pk && !!accountId && !!s.config?.stripe.configured;
  const cards = u?.paymentMethods ?? [];
  const [choice, setChoice] = useState<string>(cards[0]?.id ?? 'new');
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const cardRef = useRef<NewCardHandle>(null);
  const pay = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      if (!stripeOn) throw new Error("Card payments aren't switched on here yet.");
      const p = { kind: 'order' as const, id: orderId, cardPence: amountPence, requiresPayment: true };
      if (choice !== 'new') await paySavedCard(pk, accountId, p, choice);
      else {
        if (!cardRef.current) throw new Error('The card form is still loading');
        await payNewCard(pk, accountId, p, await cardRef.current.submit(), s.rememberCard);
      }
      s.toast('Paid. The change is confirmed.');
      await s.refreshMe();
      onPaid();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Payment failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card col" style={{ padding: 14, gap: 10 }}>
      <div className="card rows payrows" role="radiogroup" aria-label="Pay with">
        {cards.map((c) => <button type="button" className="payrow" role="radio" aria-checked={choice === c.id} key={c.id} onClick={() => setChoice(c.id)}><span className="radio2" /><span className="grow col" style={{ gap: 2 }}><b>{c.label}</b></span><span className="paymark visa">{(c.brand ?? 'card').toUpperCase()}</span></button>)}
        <button type="button" className="payrow" role="radio" aria-checked={choice === 'new'} onClick={() => setChoice('new')}><span className="radio2" /><span className="grow col" style={{ gap: 2 }}><b>{cards.length ? 'Another card' : 'Card'}</b></span><span className="paymark card">CARD</span></button>
      </div>
      {choice === 'new' && stripeOn && <NewCardForm ref={cardRef} pk={pk} remember={s.rememberCard} onRemember={s.setRememberCard} onReady={() => setReady(true)} reveal />}
      {err && <p className="err" style={{ margin: 0 }}>{err}</p>}
      <button type="button" className="yellow" disabled={busy || (choice === 'new' && !ready)} onClick={pay}>{busy ? 'One moment…' : `Pay ${fmt(amountPence)} to confirm`}</button>
    </div>
  );
}

function BookedFx({ boxes, when, refused, onDone }: { boxes: number; when: string; refused: boolean; onDone: () => void }) {
  const [b] = useState(() => burst(30, PALETTE));
  useEffect(() => {
    const t = setTimeout(onDone, reduced() ? 900 : refused ? 4000 : 2600);
    return () => clearTimeout(t);
  }, [onDone, refused]);
  return (
    <div id="fx"><div className="fx placed" onClick={onDone}>
      {!refused && <Svg html={b} />}
      {!refused && <svg className="tickdraw" width="96" height="96" viewBox="0 0 80 80" aria-hidden="true"><circle cx="40" cy="40" r="34" fill="none" stroke="#ffd84d" strokeWidth="6" /><path d="M25 41l10 10 20-22" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" /></svg>}
      <h2 className="rise" style={{ ['--d' as string]: '.45s' }}>{refused ? 'We could not book this' : 'Catering booked'}</h2>
      <p className="rise" style={{ ['--d' as string]: '.6s' }}>{refused ? 'The slot went while you paid. Your card has been refunded in full. Pick another window and try again.' : <>{when} · {boxes} box{boxes === 1 ? '' : 'es'}<br />We'll text the exact arrival time when it's packed.</>}</p>
      <span className="skip">Tap to continue</span>
    </div></div>
  );
}


